import { useGesture } from '@use-gesture/react';
import { clsx } from 'clsx';
import {
  type ComponentPropsWithoutRef,
  createContext,
  forwardRef,
  memo,
  type PropsWithChildren,
  useContext,
  useRef,
} from 'react';

import {
  ContentSlot,
  extractSlotsProps,
  PanelSlot,
  ResizeHandleSlot,
} from './slots.js';
import * as styles from './SplitLayout.sty.js';
import { useSplitSizing } from './useSplitSizing.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';

/** shell component for referencial stability - must not use any context or props */
const PanelShell = memo(({ children }: PropsWithChildren) => {
  return children;
});

/** shell component for referencial stability - must not use any context or props */
const ContentShell = memo(({ children }: PropsWithChildren) => {
  return children;
});

/** shell component for referencial stability - must not use any context or props */
const ResizeHandleShell = memo(({ children }: PropsWithChildren) => {
  return children;
});

interface PanelColumnProps
  extends PropsWithChildren,
    ComponentPropsWithoutRef<'div'> {}

const PanelColumn = forwardRef<HTMLDivElement, PanelColumnProps>(
  (props, forwardedRef) => {
    const { children, className, style, ...rest } = props;

    return (
      <div
        {...rest}
        ref={forwardedRef}
        className={clsx(styles.column, className)}
        style={style}
      >
        {children}
      </div>
    );
  },
);

interface ContentColumnProps
  extends PropsWithChildren,
    ComponentPropsWithoutRef<'div'> {}

const ContentColumn = forwardRef<HTMLDivElement, ContentColumnProps>(
  (props, forwardedRef) => {
    const { children, className, style, ...rest } = props;

    return (
      <div
        {...rest}
        ref={forwardedRef}
        className={clsx(styles.column, className)}
        style={style}
      >
        {children}
      </div>
    );
  },
);

const ResizeHandle = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>((props, forwardedRef) => {
  const { onDrag, onDragStart, onDragEnd, panelId, ariaLabel, ariaLabelledBy } =
    useContext(ActionsContext);
  const ref = useRef<HTMLDivElement>(null);
  const mergedRefs = useMergeRefs([ref, forwardedRef]);

  const { focusProps, focusClassName } = useFocusRing({
    focusWithin: true,
    ignoreModality: true,
  });

  useGesture(
    {
      onDragStart,
      onDragEnd,
      onDrag: (state) => {
        onDrag(state.movement[0]);
      },
    },
    { target: ref },
  );

  return (
    <div
      {...props}
      {...focusProps}
      role="separator"
      ref={mergedRefs}
      className={clsx(styles.resizeHandle, focusClassName)}
      tabIndex={0}
      aria-controls={panelId}
      aria-label={ariaLabelledBy ? undefined : ariaLabel}
      aria-labelledby={ariaLabelledBy}
    />
  );
});

const ActionsContext = createContext<{
  onDragStart: () => void;
  onDrag: (v: number) => void;
  onDragEnd: () => void;
  panelId: string;
  ariaLabel: string | undefined;
  ariaLabelledBy: string | undefined;
}>({
  onDragStart: () => undefined,
  onDrag: () => undefined,
  onDragEnd: () => undefined,
  panelId: '',
  ariaLabel: undefined,
  ariaLabelledBy: undefined,
});

/** @internal */
export interface SplitLayoutProps
  extends PropsWithChildren,
    BehaviorTrackingProps,
    AriaLabelingProps,
    StylingProps,
    MaskingProps,
    DataTestId {}

const SplitLayoutRoot = memo(
  forwardRef<HTMLDivElement, SplitLayoutProps>((props, forwardedRef) => {
    const { children, className: rootClassname, ...rootProps } = props;

    const [behavioralTrackingProps, restRootProps] =
      useBehavioralTrackingProps(rootProps);

    const slots = extractSlotsProps(children);

    const rootRef = useRef<HTMLDivElement>(null);
    const rootMergedRef = useMergeRefs([forwardedRef, rootRef]);

    const generatedPanelId = useId();
    const panelId = slots.panel.domProps.id ?? generatedPanelId;

    const panelRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const panelMergedRef = useMergeRefs([panelRef, slots.panel.ref]);
    const contentMergedRef = useMergeRefs([contentRef, slots.content?.ref]);

    const sizing = useSplitSizing({
      panel: {
        min: slots.panel.config.minWidth,
        max: slots.panel.config.maxWidth,
        default: slots.panel.config.defaultWidth,
        position: slots.panel.config.position,
        resizable: slots.panel.config.resizable,
        onResize: slots.panel.config.onResize,
      },
      content: {
        min: slots.content?.config.minWidth ?? 0,
      },
      refs: {
        root: rootRef,
        panel: panelRef,
        content: contentRef,
      },
    });

    const isDefaultPosition = slots.panel.config.position === 'start';
    const activeSizing = isDefaultPosition ? sizing.panel : sizing.content;

    const panelColumn = (
      <PanelColumn
        {...slots.panel.domProps}
        id={panelId}
        style={{ ...slots.panel.domProps.style, width: sizing.panel.widthCss }}
        ref={panelMergedRef}
      >
        <PanelShell>{slots.panel.children}</PanelShell>
      </PanelColumn>
    );

    const contentColumn = slots.content ? (
      <ContentColumn
        {...slots.content.domProps}
        style={{
          ...slots.content.domProps.style,
          width: sizing.content.widthCss,
          // content needs an explicit minWidth when it is the leading column
          // (end position) to prevent it from collapsing before the resize handle
          minWidth: isDefaultPosition
            ? undefined
            : slots.content.config.minWidth,
        }}
        ref={contentMergedRef}
      >
        <ContentShell>{slots.content.children}</ContentShell>
      </ContentColumn>
    ) : null;

    const resizeHandleDecorations = slots.resizeHandle?.children ? (
      <div className={styles.handleDecorations}>
        <ResizeHandleShell>{slots.resizeHandle.children}</ResizeHandleShell>
      </div>
    ) : null;

    const resizeHandle = slots.panel.config.resizable ? (
      <div
        {...slots.resizeHandle?.domProps}
        className={clsx(
          styles.handleColumn,
          slots.resizeHandle?.domProps?.className,
        )}
        ref={slots.resizeHandle?.ref}
      >
        <ResizeHandle
          aria-valuemin={activeSizing.minPx}
          aria-valuemax={
            isFinite(activeSizing.maxPx) ? activeSizing.maxPx : undefined
          }
          aria-valuenow={activeSizing.width}
          aria-valuetext={`${activeSizing.width}px`}
          aria-orientation="vertical"
        />
        {resizeHandleDecorations}
      </div>
    ) : null;

    return (
      <div
        {...restRootProps}
        {...behavioralTrackingProps}
        className={clsx(styles.layout, rootClassname)}
        ref={rootMergedRef}
      >
        <ActionsContext.Provider
          value={{
            ...sizing.dragActions,
            panelId,
            ariaLabel:
              slots.resizeHandle?.domProps['aria-label'] ??
              slots.panel.config.label,
            ariaLabelledBy: slots.resizeHandle?.domProps['aria-labelledby'],
          }}
        >
          {isDefaultPosition ? panelColumn : contentColumn}
          {resizeHandle}
          {isDefaultPosition ? contentColumn : panelColumn}
        </ActionsContext.Provider>
      </div>
    );
  }),
);
SplitLayoutRoot.displayName = 'SplitLayout.Root';

/**
 * Internal: The accessibility approach to SplitLayout is inspired by the Window Splitter ARIA pattern.
 * While we dont follow it 100%, most aria attributes and intraction should adhere to this pattern.
 * https://www.w3.org/WAI/ARIA/apg/patterns/windowsplitter/
 */

/** @internal */
export const SplitLayout = Object.assign(SplitLayoutRoot, {
  Panel: PanelSlot,
  Content: ContentSlot,
  ResizeHandle: ResizeHandleSlot,
});
